/**
 * 移动端文件管理页：上传图片 / PDF、列表、复制 URL、删除
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Dialog,
  Empty,
  InfiniteScroll,
  SearchBar,
  Skeleton,
  Tabs,
  Tag,
} from 'antd-mobile';
import { UploadOutline, DeleteOutline, FileOutline } from 'antd-mobile-icons';
import { adminFileApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

const ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf';
const PAGE_SIZE = 15;

function formatSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}

function fullUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return window.location.origin + url;
}

export default function MobileFileManagePage() {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [fileType, setFileType] = useState('');
  const [refreshFlag, setRefreshFlag] = useState(0);

  useEffect(() => {
    fetchList(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, fileType, refreshFlag]);

  const fetchList = async (pageNum, append) => {
    if (loadingMore && append) return;
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);

      const data = await adminFileApi.list({
        page: pageNum,
        pageSize: PAGE_SIZE,
        keyword: searchKeyword || undefined,
        fileType: fileType || undefined,
      });
      const items = data?.items || [];
      const totalData = data?.total || 0;

      if (append) {
        setFiles((prev) => {
          const updated = [...prev, ...items];
          setHasMore(updated.length < totalData);
          return updated;
        });
      } else {
        setFiles(items);
        setHasMore(items.length < totalData);
      }
      setTotal(totalData);
      setPage(pageNum);
    } catch (err) {
      Toast.show({ icon: 'fail', content: '加载失败' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = async () => {
    await fetchList(page + 1, true);
  };

  const handlePickFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    for (const file of list) {
      if (file.size > 20 * 1024 * 1024) {
        Toast.show({ icon: 'fail', content: `${file.name} 超过 20MB` });
        continue;
      }
      try {
        setUploading(true);
        await adminFileApi.upload(file);
        Toast.show({ icon: 'success', content: `${file.name} 上传成功` });
      } catch (err) {
        Toast.show({ icon: 'fail', content: err?.response?.data?.message || `${file.name} 上传失败` });
      } finally {
        setUploading(false);
      }
    }
    setRefreshFlag((v) => v + 1);
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(fullUrl(url));
      Toast.show({ icon: 'success', content: 'URL 已复制' });
    } catch (e) {
      Toast.show({ icon: 'fail', content: '复制失败' });
    }
  };

  const handleDelete = (record) => {
    Dialog.confirm({
      title: '删除文件',
      content: `确认删除「${record.originalFilename}」？磁盘文件会同时删除，无法恢复。`,
      confirmText: '删除',
      cancelText: '取消',
      onConfirm: async () => {
        try {
          await adminFileApi.delete(record.id);
          Toast.show({ icon: 'success', content: '已删除' });
          setRefreshFlag((v) => v + 1);
        } catch (err) {
          Toast.show({ icon: 'fail', content: err?.response?.data?.message || '删除失败' });
        }
      },
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', paddingBottom: 60 }}>
      {/* 上传区 */}
      <div style={{ padding: 12, background: '#fff', borderBottom: '1px solid #eee' }}>
        <Button
          block
          color="primary"
          loading={uploading}
          onClick={handlePickFile}
        >
          <UploadOutline style={{ marginRight: 6 }} /> 上传图片 / PDF（最大 20MB）
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {/* 搜索 + 类型 Tab */}
      <div style={{ padding: 12, background: '#fff', borderBottom: '1px solid #eee' }}>
        <SearchBar
          placeholder="按文件名搜索"
          value={keyword}
          onChange={setKeyword}
          onSearch={(val) => setSearchKeyword(val)}
          onClear={() => { setKeyword(''); setSearchKeyword(''); }}
          style={{ marginBottom: 8 }}
        />
        <Tabs activeKey={fileType} onChange={setFileType}>
          <Tabs.Tab title="全部" key="" />
          <Tabs.Tab title="图片" key="image" />
          <Tabs.Tab title="PDF" key="pdf" />
        </Tabs>
      </div>

      {/* 列表 */}
      <div style={{ padding: 12 }}>
        {loading ? (
          <>
            <Skeleton.Title rows={2} />
            <Skeleton.Paragraph lineCount={5} />
          </>
        ) : files.length === 0 ? (
          <Empty description="暂无文件" style={{ padding: 40 }} />
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
              共 {total} 个文件
            </div>
            {files.map((f) => (
              <Card key={f.id} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  {f.fileType === 'image' ? (
                    <a href={fullUrl(f.url)} target="_blank" rel="noreferrer">
                      <img
                        src={fullUrl(f.url)}
                        alt={f.originalFilename}
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: 'cover',
                          borderRadius: 4,
                          border: '1px solid #eee',
                          flexShrink: 0,
                        }}
                      />
                    </a>
                  ) : (
                    <a
                      href={fullUrl(f.url)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        width: 64,
                        height: 64,
                        background: '#fef0ef',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FileOutline style={{ fontSize: 32, color: '#C44536' }} />
                    </a>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={f.originalFilename}
                    >
                      {f.originalFilename}
                    </div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                      <Tag color={f.fileType === 'image' ? 'success' : 'danger'} style={{ marginRight: 6 }}>
                        {f.fileType === 'image' ? '图片' : 'PDF'}
                      </Tag>
                      {formatSize(f.size)}
                    </div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      {f.uploadedAt?.replace('T', ' ')?.slice(0, 16) || '-'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Button
                    size="small"
                    block
                    onClick={() => handleCopy(f.url)}
                  >
                    复制 URL
                  </Button>
                  <Button
                    size="small"
                    block
                    color="danger"
                    onClick={() => handleDelete(f)}
                  >
                    <DeleteOutline /> 删除
                  </Button>
                </div>
              </Card>
            ))}
            <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />
          </>
        )}
      </div>
    </div>
  );
}
